import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { IFlowWizardProps } from "./types";
import { View, Animated } from "react-native";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { useAnimateTransition } from "@shared/hooks";
import WizardNavigation from "./WizardNavigation";
import { Head } from "../head";
import { Loading } from "../loading";
import { Telemetry } from "app/services/telemetry/Telemetry";
import { useFlowWizardWebSocket } from "@shared/hooks/useFlowWizardWebSocket";
import { MeasurementErrorState } from "@shared/components/measurement";

export interface FlowWizardHandle {
  prev: () => void;
  goToStep: (index: number) => void;
  startWebSocketListening: () => void;
  cancelWebSocketListening: () => void;
}

function FlowWizardInner<T extends Record<string, any>>({
  steps,
  backText = "buttons.back",
  nextText = "buttons.continue",
  finishText = "buttons.finalize",
  onFinish,
  onStepChange,
  initialStep = 0,
  asyncState,
  layout = "horizontal",
  customButtons,
  webSocketConfig,
  staticTopContent,
  alwaysShowBackButton = false,
  onWaitingStateChange,
}: IFlowWizardProps<T>, ref: React.Ref<FlowWizardHandle>) {
  const [current, setCurrent] = useState(initialStep);
  const [stepsData, setStepsData] = useState<T>({} as T);
  const { t } = dynamicTranslations();
  const { slideAnim, fadeAnim, animateStepTransition } = useAnimateTransition();

  const {
    isWaitingForMeasurement,
    isTimedOut,
    hasDeviceError,
    clearDeviceError,
    retryWebSocketListening,
    startWebSocketListening,
    cancelWebSocketListening,
    shouldStartListeningOnNext
  } = useFlowWizardWebSocket({
    webSocketConfig,
    steps,
    onNavigateToResult: (stepIndex) => {
      animateStepTransition('forward', () => {
        setCurrent(stepIndex);
        onStepChange && onStepChange(current, stepIndex);
      });
    }
  });

  const currentStep = steps[current];

  const needAsyncCheck = () => {
    return (
      asyncState &&
      asyncState[currentStep.key] !== undefined &&
      steps[current].onContinue
    );
  };
  useEffect(() => {
    if (needAsyncCheck() && asyncState![currentStep.key]!.isSuccess) {
      goToNextStep(steps[current].form?.getFieldsValue());
    }
  }, [asyncState]);

  const goToNextStep = async (currentValues: any) => {
    setStepsData((prev) => ({
      ...prev,
      [steps[current].key]: currentValues
    }));
    const currentStep = current;
    const nextStep = Math.min(currentStep + 1, steps.length - 1);

    animateStepTransition('forward', () => {
      setCurrent(nextStep);
      onStepChange && onStepChange(currentStep, nextStep);
    });
  };

  const next = async () => {
    if (steps[current].isNextDisabled) {
      steps[current].onValidationFail?.();
      return;
    }

    try {
      await steps[current].form?.validateFields();
      const currentValues = steps[current].form?.getFieldsValue();

      const currentStepKey = steps[current].key;
      if (shouldStartListeningOnNext(currentStepKey)) {
        startWebSocketListening();
        return;
      }

      if (needAsyncCheck() && !asyncState![currentStep.key]!.isSuccess) {
        return steps[current].onContinue!();
      } else {
        goToNextStep(currentValues);
      }
    } catch (error) {
      Telemetry.error(error as Error, {
        event: 'ui.form.validation.failed',
        domain: 'ui',
        context: { source: 'FlowWizard', step: steps[current]?.key }
      });
    }
  };
  const prev = useCallback(() => {
    const currentValues = steps[current].form?.getFieldsValue();

    setStepsData((prevData) => ({
      ...prevData,
      [steps[current].key]: currentValues
    }));
    const currentStep = current;
    const prevStep = Math.max(currentStep - 1, 0);

    animateStepTransition('backward', () => {
      setCurrent(prevStep);
      onStepChange && onStepChange(currentStep, prevStep);
    });
  }, [current, steps, onStepChange, animateStepTransition]);

  const goToStep = useCallback((index: number) => {
    const currentStep = current;
    animateStepTransition('forward', () => {
      setCurrent(index);
      onStepChange && onStepChange(currentStep, index);
    });
  }, [current, onStepChange, animateStepTransition]);

  useImperativeHandle(ref, () => ({ prev, goToStep, startWebSocketListening, cancelWebSocketListening }), [prev, goToStep, startWebSocketListening, cancelWebSocketListening]);

  useEffect(() => {
    onWaitingStateChange?.(isWaitingForMeasurement || isTimedOut);
  }, [isWaitingForMeasurement, isTimedOut, onWaitingStateChange]);

  const handleFinish = async () => {
    if (steps[current].isFinishDisabled) {
      steps[current].onValidationFail?.();
      return;
    }

    try {
      await steps[current].form?.validateFields();
      const finalStepData = steps[current].form?.getFieldsValue();
      const allData = {
        ...stepsData,
        [steps[current].key]: finalStepData
      } as T;
      setStepsData(allData);

      onFinish(allData);
    } catch (error) {
      Telemetry.error(error as Error, {
        event: 'ui.form.final_validation.failed',
        domain: 'ui',
        context: { source: 'FlowWizard', step: steps[current]?.key }
      });
    }
  };

  const CurrentStep = steps[current].component;
  const containerClasses = `flex-1 flex-col`;

  const retryAfterDeviceError = () => {
    const beforeKey = webSocketConfig?.beforeMeasurementStepKey ?? 'beforeMeasurementStep';
    const beforeIndex = steps.findIndex(step => step.key === beforeKey);
    clearDeviceError();

    if (beforeIndex === -1) {
      retryWebSocketListening();
      return;
    }

    animateStepTransition('backward', () => {
      setCurrent(beforeIndex);
      onStepChange && onStepChange(current, beforeIndex);
    });
  };

  if (hasDeviceError) {
    return (
      <MeasurementErrorState
        onRetry={retryAfterDeviceError}
        onGoBack={customButtons?.backButton?.onPress}
        goBackText={customButtons?.backButton?.text}
      />
    );
  }

  if (isWaitingForMeasurement) {
    return <Loading message={t('measurement.waitingMeasurement')} />;
  }

  if (isTimedOut) {
    return <Loading message={t('measurement.deviceTimeout')} onRetry={retryWebSocketListening} />;
  }

  return (
    <View className={containerClasses}>
      {steps[current].title && (
        <Head type="h2">
          {t(steps[current].title)}
        </Head>
      )}
      {staticTopContent}
      <View className="flex-1 overflow-hidden">
        <Animated.View
          style={{
            flex: 1,
            transform: [{ translateX: slideAnim }],
            opacity: fadeAnim,
          }}
        >
          <CurrentStep
            context={steps[current].context}
            form={steps[current].form!}
            initialValues={
              stepsData[steps[current].key] || steps[current].initialValues!
            }
            translations={steps[current].translations}
          />
        </Animated.View>
      </View>
      <View className="-mx-8 px-8 pt-4 border-t border-gray-200 bg-white">
        <WizardNavigation
          isLoading={
            ((asyncState &&
              asyncState[currentStep.key] !== undefined &&
              asyncState[currentStep.key]?.isLoading) ??
              false)
          }
          isLastStep={current === steps.length - 1}
          isFirstStep={current === 0}
          backText={t(backText)}
          nextText={t(nextText)}
          finishText={t(finishText)}
          onNext={next}
          onPrev={prev}
          onFinish={handleFinish}
          layout={layout}
          customButtons={customButtons}
          showBackButton={
            (steps[current].showBackButton !== false && (alwaysShowBackButton || current > 0)) ||
            (customButtons?.backButton !== undefined && steps[current].showBackButton !== false)
          }
          showNextButton={steps[current].showNextButton !== false}
          disableNextButton={steps[current].isNextDisabled && !steps[current].onValidationFail}
          disableFinishButton={steps[current].isFinishDisabled && !steps[current].onValidationFail}
        />
      </View>
    </View>
  );
}

const FlowWizard = forwardRef(FlowWizardInner) as <T extends Record<string, any>>(
  props: IFlowWizardProps<T> & { ref?: React.Ref<FlowWizardHandle> }
) => ReturnType<typeof FlowWizardInner>;

export default FlowWizard;